<script>
	import Navbar from '../components/Navbar.svelte';
	import { fetchLog, getStagedUploads, fetchExplorerTags, stageUpload, stageCollection, fetchWebhookUrl } from '../lib/api.js';
	import { formatFileSize } from '../lib/formatting.js';
	import { Upload, Mail, Link2, RefreshCw, X, Check } from 'lucide-svelte';

	export let navigate;

	// --- Tab state ---
	let activeTab = 'upload'; // upload | email | integrations

	// --- Upload tab state ---
	let topics = [];
	let staged = [];
	let stagedFiles = [];
	let dragOver = false;
	let fileInput;
	let processAsCollection = false;
	let uploadState = 'idle'; // idle | uploading | done | error
	let uploadPhase = ''; // staging | processing
	let uploadError = '';
	let stagingProgress = { done: 0, total: 0, errors: [] };

	$: zipCount = stagedFiles.filter(f => f.name.toLowerCase().endsWith('.zip')).length;
	$: looseCount = stagedFiles.length - zipCount;
	$: expectHint = (() => {
		if (stagedFiles.length === 0) return 'Drop files above to get started.';
		if (processAsCollection && looseCount > 0)
			return `${looseCount} file${looseCount !== 1 ? 's' : ''} will be processed together — the pipeline will determine placement automatically.`;
		if (stagedFiles.length === 1 && zipCount === 1)
			return 'A new topic will be created with a set of documents based on your zip.';
		if (zipCount > 0 && looseCount === 0)
			return 'A new topic will be created for each zip.';
		if (stagedFiles.length === 1)
			return 'A new document will be created. The pipeline will find or create the right topic.';
		return `${looseCount} files will each be processed independently.`;
	})();

	function handleDrop(e) { dragOver = false; Array.from(e.dataTransfer.files).forEach(stageFile); }
	function handleFileSelect(e) { Array.from(e.target.files).forEach(stageFile); e.target.value = ''; }
	function stageFile(file) {
		if (stagedFiles.find(f => f.name === file.name && f.size === file.size)) return;
		stagedFiles = [...stagedFiles, file];
	}
	function removeStaged(index) { stagedFiles = stagedFiles.filter((_, i) => i !== index); }

	async function handleSubmit() {
		if (stagedFiles.length === 0) return;
		uploadState = 'uploading';
		uploadPhase = 'staging';
		const isZip = f => f.name.toLowerCase().endsWith('.zip');

		if (processAsCollection && !stagedFiles.some(isZip)) {
			stagingProgress = { done: 0, total: stagedFiles.length, errors: [] };
			try {
				await stageCollection(stagedFiles);
				stagingProgress.done = stagedFiles.length;
				stagingProgress = stagingProgress;
				uploadPhase = 'processing';
			} catch (err) { uploadError = err.message; uploadState = 'error'; }
			return;
		}

		stagingProgress = { done: 0, total: stagedFiles.length, errors: [] };
		try {
			await Promise.all(stagedFiles.map(async (file) => {
				try { await stageUpload(file); stagingProgress.done++; stagingProgress = stagingProgress; }
				catch (err) { stagingProgress.errors.push(file.name); stagingProgress = stagingProgress; throw err; }
			}));
			uploadPhase = 'processing';
		} catch (err) {
			if (stagingProgress.done > 0) { uploadPhase = 'processing'; }
			else { uploadError = err.message; uploadState = 'error'; }
		}
	}

	function resetUpload() {
		uploadState = 'idle'; uploadError = ''; stagedFiles = []; uploadPhase = '';
		stagingProgress = { done: 0, total: 0, errors: [] }; processAsCollection = false;
		refreshAll();
	}

	// --- Activity log state ---
	let logEntries = [];
	let logLoading = true;
	let logFilter = 'upload'; // matches activeTab default; all | email | upload | slack
	let expandedGroupKey = null; // key of expanded parent row

	// --- Load data ---
	async function loadTopics() {
		try {
			const data = await fetchExplorerTags();
			topics = (data || []).map(t => t.name);
		} catch { topics = []; }
	}

	async function loadStaged() {
		try {
			const data = await getStagedUploads();
			staged = (data.uploads || []).filter(u => u.status === 'pending' || u.status === 'processing');
		} catch { staged = []; }
	}

	async function loadLog() {
		logLoading = true;
		try {
			const params = logFilter !== 'all' ? { type: logFilter } : {};
			const rows = await fetchLog(params);
			logEntries = rows.map(r => ({
				id: r.id,
				timestamp: r.created_at,
				type: r.type,
				status: r.status,
				summary: r.summary,
				data: r.data || {}
			}));
		} catch { logEntries = []; }
		logLoading = false;
	}

	// Initial load
	loadTopics();
	loadStaged();
	loadLog();
	loadWebhookUrl();

	// Sync log filter to active tab
	const TAB_LOG_MAP = { upload: 'upload', email: 'email', integrations: 'slack' };
	$: logFilter = TAB_LOG_MAP[activeTab] || 'all';

	// Reload log when filter changes
	$: logFilter, (logPage = 0, loadLog());

	// Build grouped log: upload entries grouped by filename, others flat
	$: groupedLog = buildGroupedLog(logEntries, staged);

	function buildGroupedLog(entries, stagedItems) {
		// Inject synthetic entries for pending/processing staged uploads
		const merged = [...entries];
		for (const s of stagedItems) {
			merged.push({
				id: 'staged-' + s.id,
				timestamp: s.created_at,
				type: 'upload',
				status: s.status === 'processing' ? 'ok' : 'pending',
				summary: s.status === 'processing'
					? `Processing: "${s.filename || s.original_filename}"`
					: `Queued: "${s.filename || s.original_filename}"`,
				data: { filename: s.filename || s.original_filename, uploadId: s.id, staged: true },
				_staged: true
			});
		}

		// Sort newest first
		merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

		// Group upload entries by filename; email/slack stay flat
		const items = [];
		const uploadGroups = new Map(); // filename -> entries[]

		for (const e of merged) {
			if (e.type === 'upload' && e.data?.filename) {
				const key = e.data.filename.toLowerCase();
				if (!uploadGroups.has(key)) uploadGroups.set(key, []);
				uploadGroups.get(key).push(e);
			} else {
				items.push({ kind: 'flat', entry: e });
			}
		}

		// Convert upload groups into parent rows
		for (const [key, group] of uploadGroups) {
			// Latest entry is the parent (already sorted newest-first)
			const latest = group[0];
			const hasPending = group.some(e => e._staged);
			items.push({
				kind: 'parent',
				key,
				filename: latest.data.filename,
				timestamp: latest.timestamp,
				status: hasPending ? 'pending' : latest.status,
				summary: latest.summary,
				children: group
			});
		}

		// Re-sort everything by timestamp
		items.sort((a, b) => {
			const ta = a.kind === 'parent' ? a.timestamp : a.entry.timestamp;
			const tb = b.kind === 'parent' ? b.timestamp : b.entry.timestamp;
			return new Date(tb) - new Date(ta);
		});

		return items;
	}

	// Pagination
	const PAGE_SIZE = 25;
	let logPage = 0;
	$: totalPages = Math.max(1, Math.ceil(groupedLog.length / PAGE_SIZE));
	$: pagedLog = groupedLog.slice(logPage * PAGE_SIZE, (logPage + 1) * PAGE_SIZE);

	function toggleGroup(key) {
		expandedGroupKey = expandedGroupKey === key ? null : key;
	}

	function refreshAll() {
		loadStaged();
		loadLog();
		loadTopics();
	}

	// --- Helpers ---
	function formatTs(dateStr) {
		const d = new Date(dateStr);
		const now = new Date();
		const diffMs = now - d;
		const diffMin = Math.floor(diffMs / 60000);
		if (diffMin < 1) return 'just now';
		if (diffMin < 60) return diffMin + 'm ago';
		const diffHr = Math.floor(diffMin / 60);
		if (diffHr < 24) return diffHr + 'h ago';
		const diffDay = Math.floor(diffHr / 24);
		if (diffDay === 1) return 'yesterday';
		if (diffDay < 7) return diffDay + 'd ago';
		const mo = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${d.getFullYear()}-${mo}-${day}`;
	}

	function outcomeLabel(entry) {
		if (entry.status === 'error') return 'failed';
		const s = entry.summary.toLowerCase();
		if (s.includes('merged')) return 'merged';
		if (s.includes('created')) return 'created';
		if (s.includes('updated')) return 'updated';
		if (s.includes('skipped')) return 'skipped';
		if (s.includes('answered')) return 'answered';
		if (s.includes('stored')) return 'stored';
		if (s.includes('staged')) return 'staged';
		return 'ok';
	}

	// --- Email tab ---
	let inboxes = [
		{
			id: 'docs',
			name: 'Document Inbox',
			description: 'Send any document to this address. Attachments (PDF, DOCX, images) are extracted automatically.',
			address: 'docs@adrasi.com',
			status: 'active',
			canRegenerate: false,
			hasToggle: true
		},
		{
			id: 'code-feed',
			name: 'Code Feed',
			description: 'Subscribe this inbox to GitHub, Azure DevOps, or Jira email notifications. PRs, commits, and reviews are absorbed as documents.',
			address: 'diff-a7f3c9e2b14d@adrasi.com',
			status: 'inactive',
			canRegenerate: true,
			hasToggle: true,
			handover: null
		}
	];

	const githubActionYaml = `name: ADRASI Sync\non:\n  push:\n    branches: [main]\n\njobs:\n  sync:\n    runs-on: ubuntu-latest\n    steps:\n      - name: Notify ADRASI\n        env:\n          ADRASI_URL: \${{ secrets.ADRASI_WEBHOOK_URL }}\n          COMMIT_MSG: \${{ github.event.head_commit.message }}\n        run: |\n          jq -n \\\\\n            --arg repo "$GITHUB_REPOSITORY" \\\\\n            --arg sha "$GITHUB_SHA" \\\\\n            --arg branch "$GITHUB_REF_NAME" \\\\\n            --arg actor "$GITHUB_ACTOR" \\\\\n            --arg message "$COMMIT_MSG" \\\\\n            '{repo:$repo,sha:$sha,branch:$branch,actor:$actor,message:$message}' \\\\\n          | curl -sf -X POST "$ADRASI_URL" \\\\\n              -H "Content-Type: application/json" \\\\\n              -d @-`;

	// --- Webhook URL state ---
	let webhookUrl = null;
	let webhookLoading = true;

	async function loadWebhookUrl() {
		webhookLoading = true;
		try { webhookUrl = await fetchWebhookUrl(); } catch { webhookUrl = null; }
		webhookLoading = false;
	}

	let copiedKey = null;
	async function copyToClipboard(text, key) {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
		} catch {
			const ta = document.createElement('textarea');
			ta.value = text;
			document.body.appendChild(ta);
			ta.select();
			document.execCommand('copy');
			document.body.removeChild(ta);
		}
		copiedKey = key;
		setTimeout(() => { copiedKey = null; }, 2000);
	}

	// Regenerate with handover
	let confirmingRegen = null;
	let regenerating = null;

	function promptRegenerate(inbox) { confirmingRegen = inbox.id; }
	function cancelRegenerate() { confirmingRegen = null; }

	function confirmRegenerate(inbox) {
		confirmingRegen = null;
		regenerating = inbox.id;
		setTimeout(() => {
			const hex = Array.from({ length: 12 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
			const oldAddress = inbox.address;
			inbox.address = `diff-${hex}@adrasi.com`;
			inbox.status = 'active';
			const expires = new Date();
			expires.setDate(expires.getDate() + 7);
			inbox.handover = { oldAddress, expiresAt: expires.toISOString(), oldReceiving: true };
			inboxes = inboxes;
			regenerating = null;
		}, 1200);
	}

	// Toggle inbox on/off
	let togglingId = null;
	function toggleInbox(inbox) {
		togglingId = inbox.id;
		setTimeout(() => {
			inbox.status = inbox.status === 'active' ? 'disabled' : 'active';
			inboxes = inboxes;
			togglingId = null;
		}, 600);
	}

	function toggleHandoverAddress(inbox) {
		if (!inbox.handover) return;
		inbox.handover.disabled = !inbox.handover.disabled;
		if (inbox.handover.disabled) inbox.handover.oldReceiving = false;
		inboxes = inboxes;
	}

	function daysUntil(dateStr) {
		if (!dateStr) return 0;
		const ms = new Date(dateStr).getTime() - Date.now();
		return Math.max(0, Math.ceil(ms / 86400000));
	}

	function formatActivity(dateStr) {
		if (!dateStr) return 'Never';
		const ms = Date.now() - new Date(dateStr).getTime();
		const hours = Math.floor(ms / 3600000);
		if (hours < 1) return 'Just now';
		if (hours < 24) return hours + 'h ago';
		const days = Math.floor(hours / 24);
		if (days === 1) return 'Yesterday';
		return days + 'd ago';
	}
</script>

<Navbar {navigate} active="connect" />

<div class="connect-page">
	<div class="connect-container">

		<!-- TOP HALF: Tabs + Content -->
		<div class="connect-top">
			<div class="tab-bar">
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<div class="tab" class:tab-active={activeTab === 'upload'} on:click={() => activeTab = 'upload'}>
					<Upload size={14} strokeWidth={1.5} />
					<span>Upload</span>
				</div>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<div class="tab" class:tab-active={activeTab === 'email'} on:click={() => activeTab = 'email'}>
					<Mail size={14} strokeWidth={1.5} />
					<span>Email</span>
				</div>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<div class="tab" class:tab-active={activeTab === 'integrations'} on:click={() => activeTab = 'integrations'}>
					<Link2 size={14} strokeWidth={1.5} />
					<span>Integrations</span>
				</div>
			</div>

			<div class="tab-content">
				{#if activeTab === 'upload'}
					<div class="tab-panel">
						{#if uploadState === 'idle'}
							<div class="upload-split">
								<!-- LEFT: Drop zone / file list -->
								<div class="upload-left">
									{#if stagedFiles.length === 0}
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<div class="drop-zone drop-zone-fill"
											class:drag-over={dragOver}
											on:dragover|preventDefault={() => dragOver = true}
											on:dragleave={() => dragOver = false}
											on:drop|preventDefault={handleDrop}
											on:click={() => fileInput.click()}>
											<span class="drop-icon"><Upload size={24} strokeWidth={1.5} /></span>
											<p class="drop-title">Drop files here</p>
											<p class="drop-subtitle">PDF, DOCX, Markdown, text, or zip</p>
										</div>
									{:else}
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<div class="file-list-zone file-list-fill"
											on:dragover|preventDefault={() => dragOver = true}
											on:dragleave={() => dragOver = false}
											on:drop|preventDefault={handleDrop}
											class:drag-over={dragOver}>
											<div class="file-table">
												{#each stagedFiles as file, i}
													<div class="file-row">
														<span class="file-name" title={file.name}>{file.name.length > 36 ? file.name.slice(0, 36) + '...' : file.name}</span>
														<span class="file-size">{formatFileSize(file.size)}</span>
														<button class="file-remove" on:click={() => removeStaged(i)} title="Remove">
															<X size={10} strokeWidth={1.5} />
														</button>
													</div>
												{/each}
											</div>
											<button class="add-more-btn" on:click={() => fileInput.click()}>+ add more</button>
										</div>
									{/if}
									<input bind:this={fileInput} type="file" accept=".zip,.pdf,.docx,.txt,.md,.json,.js,.ts,.jsx,.tsx,.py,.html,.css,.csv,.yaml,.yml,.xml,.sql,.sh,.go,.rs,.java,.rb,.toml,.env,.cfg,.ini,.log" on:change={handleFileSelect} multiple hidden />
								</div>

								<!-- RIGHT: Options -->
								<div class="upload-right">
									<div class="collection-toggle-row">
										<div class="collection-toggle-text">
											<span class="collection-toggle-label">Process as collection</span>
											<span class="collection-toggle-hint">Files share context and are placed together</span>
										</div>
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<button class="toggle-switch" class:on={processAsCollection} on:click={() => processAsCollection = !processAsCollection}>
											<span class="toggle-knob"></span>
										</button>
									</div>

									<div class="expect-block">
										<span class="expect-label">What to expect</span>
										<p class="expect-text">{expectHint}</p>
									</div>

									<button class="submit-btn submit-btn-full" disabled={stagedFiles.length === 0} on:click={handleSubmit}>Submit</button>
								</div>
							</div>

						{:else if uploadState === 'uploading'}
							<div class="upload-progress">
								{#if uploadPhase === 'staging'}
									<div class="upload-check uploading"><Upload size={20} strokeWidth={1.5} /></div>
									<p class="upload-status">Staging {stagingProgress.done} of {stagingProgress.total}...</p>
									<div class="progress-bar-track">
										<div class="progress-bar-fill" style="width: {stagingProgress.total ? Math.round(stagingProgress.done / stagingProgress.total * 100) : 0}%"></div>
									</div>
								{:else}
									<div class="upload-check"><Check size={20} strokeWidth={1.5} /></div>
									<p class="upload-status">{stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} queued</p>
									<p class="upload-hint">Processing in the background. Check the log below for updates.</p>
									<!-- svelte-ignore a11y-click-events-have-key-events -->
									<button class="upload-okay-btn" on:click={resetUpload}>Upload more</button>
								{/if}
							</div>

						{:else if uploadState === 'error'}
							<div class="upload-progress">
								<div class="error-icon-small">!</div>
								<p class="upload-error-msg">{uploadError}</p>
								<!-- svelte-ignore a11y-click-events-have-key-events -->
								<button class="upload-okay-btn" on:click={resetUpload}>Try again</button>
							</div>
						{/if}
					</div>

				{:else if activeTab === 'email'}
					<div class="tab-panel">
						<div class="card-grid">
							{#each inboxes as inbox (inbox.id)}
								<div class="square-card">
									<div class="inbox-head">
										<div class="inbox-head-left">
											<span class="status-dot" class:active={inbox.status === 'active'} class:inactive={inbox.status === 'inactive' || inbox.status === 'disabled'}></span>
											<span class="inbox-name">{inbox.name}</span>
										</div>
										{#if inbox.hasToggle}
											<!-- svelte-ignore a11y-click-events-have-key-events -->
											<span class="toggle-track"
												class:toggle-on={inbox.status === 'active'}
												class:toggle-off={inbox.status === 'disabled' || inbox.status === 'inactive'}
												class:toggling={togglingId === inbox.id}
												on:click={() => toggleInbox(inbox)}>
												<span class="toggle-thumb"></span>
											</span>
										{/if}
									</div>
									<p class="inbox-desc">{inbox.description}</p>

									<div class="address-block">
										<div class="address-row">
											<code class="config-address">{inbox.address}</code>
										</div>
										<div class="address-actions">
											<!-- svelte-ignore a11y-click-events-have-key-events -->
											<span class="action-btn" on:click={() => copyToClipboard(inbox.address, inbox.id)}>
												{copiedKey === inbox.id ? 'copied' : 'copy'}
											</span>
											{#if inbox.canRegenerate && !inbox.handover}
												<!-- svelte-ignore a11y-click-events-have-key-events -->
												<span class="action-btn action-regen" on:click={() => promptRegenerate(inbox)}>
													{regenerating === inbox.id ? 'regenerating...' : 'regenerate'}
												</span>
											{/if}
										</div>
									</div>

									{#if confirmingRegen === inbox.id}
										<div class="confirm-block">
											<p class="confirm-text">New address will be created. Current address works for 7 more days.</p>
											<div class="confirm-actions">
												<!-- svelte-ignore a11y-click-events-have-key-events -->
												<span class="action-btn confirm-yes" on:click={() => confirmRegenerate(inbox)}>Confirm</span>
												<!-- svelte-ignore a11y-click-events-have-key-events -->
												<span class="action-btn" on:click={cancelRegenerate}>Cancel</span>
											</div>
										</div>
									{/if}
								</div>
							{/each}
						</div>

						<div class="instructions">
							<span class="instructions-label">Routing</span>
							<div class="instruction-items">
								<div class="instruction-item">
									<span class="instruction-key">New document</span>
									<span class="instruction-val">Topic name in subject or body</span>
								</div>
								<div class="instruction-item">
									<span class="instruction-key">Update existing</span>
									<span class="instruction-val">Reply to the confirmation email</span>
								</div>
								<div class="instruction-item">
									<span class="instruction-key">Query</span>
									<span class="instruction-val">[query] in subject</span>
								</div>
								<div class="instruction-item">
									<span class="instruction-key">Store verbatim</span>
									<span class="instruction-val">[store] in subject</span>
								</div>
							</div>
						</div>
					</div>

				{:else if activeTab === 'integrations'}
					<div class="tab-panel">
						<div class="card-grid">
							<div class="square-card">
								<div class="integration-head">
									<span class="integration-name">Slack</span>
									<span class="status-dot active"></span>
								</div>
								<p class="inbox-desc">Slash commands for your workspace: /idea, /query, /research, /dev</p>
								<div class="integration-setup">
									<ol class="setup-list">
										<li>Install the ADRASI app</li>
										<li>/idea to submit, /query to search</li>
										<li>/research to submit, /dev for notes</li>
									</ol>
								</div>
							</div>

							<div class="square-card">
								<div class="integration-head">
									<span class="integration-name">GitHub</span>
									<span class="status-dot inactive"></span>
								</div>
								<p class="inbox-desc">Click to copy the code below and paste it inside your repo under Actions / Custom. This will automatically pick up code changes and update the related documents.</p>
								<div class="integration-setup">
									<!-- Webhook URL block -->
									<span class="setup-file-label">Your webhook URL</span>
									{#if webhookLoading}
										<div class="intg-copy-block intg-copy-loading">
											<span class="intg-address">Loading...</span>
										</div>
									{:else if webhookUrl}
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<div class="intg-copy-block" on:click={() => copyToClipboard(webhookUrl, 'github-url')}>
											<span class="intg-address">{webhookUrl}</span>
											<span class="intg-copy-btn">{copiedKey === 'github-url' ? 'Copied' : 'Copy'}</span>
										</div>
									{/if}
									<!-- Action script block -->
									<span class="setup-file-label" style="margin-top:10px">GitHub action script</span>
									<!-- svelte-ignore a11y-click-events-have-key-events -->
									<div class="intg-copy-block" on:click={() => copyToClipboard(githubActionYaml, 'github-action')}>
										<span class="intg-address">{copiedKey === 'github-action' ? 'Copied' : 'GitHub action script'}</span>
										<span class="intg-copy-btn">{copiedKey === 'github-action' ? '✓' : 'Copy'}</span>
									</div>
									<p class="intg-secrets-note">Add <code>ADRASI_WEBHOOK_URL</code> to your repo secrets and set it to the URL above.</p>
								</div>
							</div>

							<div class="square-card square-card-future">
								<div class="integration-head">
									<span class="integration-name">MCP Server</span>
									<span class="integration-badge">coming</span>
								</div>
								<p class="inbox-desc">Query directly from Claude, Gemini, or any MCP client</p>
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- BOTTOM HALF: Activity Log -->
		<div class="connect-bottom">
			<div class="log-header">
				<span class="log-title">Activity</span>
				<div class="log-controls">
					<span class="log-filter-label">{logFilter}</span>
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<span class="log-refresh" on:click={refreshAll}>
						<RefreshCw size={12} strokeWidth={1.5} />
					</span>
				</div>
			</div>

			<div class="log-scroll">
				{#if logLoading}
					<div class="log-empty">Loading...</div>
				{:else if groupedLog.length === 0}
					<div class="log-empty">No activity yet.</div>
				{:else}
					{#each pagedLog as item, i (item.kind === 'parent' ? item.key : item.entry.id)}
						{#if item.kind === 'parent'}
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div class="log-row log-row-parent" class:log-row-error={item.status === 'error'} class:log-row-expanded={expandedGroupKey === item.key} on:click={() => toggleGroup(item.key)}>
								<span class="log-col-ts">{formatTs(item.timestamp)}</span>
								<span class="log-col-type">upload</span>
								<span class="log-col-summary">{item.filename}</span>
								<span class="log-col-outcome" class:outcome-ok={item.status === 'ok'} class:outcome-err={item.status === 'error'} class:outcome-pending={item.status === 'pending'}>{item.status === 'pending' ? 'pending' : item.status === 'error' ? 'failed' : outcomeLabel({ status: item.status, summary: item.summary })}</span>
								<span class="log-col-count">{item.children.length}</span>
							</div>
							{#if expandedGroupKey === item.key}
								{#each item.children as child (child.id)}
									<div class="log-row log-row-child">
										<span class="log-col-ts">{formatTs(child.timestamp)}</span>
										<span class="log-col-type"></span>
										<span class="log-col-summary">{child.summary}</span>
										<span class="log-col-outcome" class:outcome-ok={child.status === 'ok'} class:outcome-err={child.status === 'error'} class:outcome-pending={child._staged}>{child._staged ? child.data?.staged ? (child.status === 'ok' ? 'processing' : 'queued') : 'ok' : outcomeLabel(child)}</span>
									</div>
								{/each}
							{/if}
						{:else}
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div class="log-row" class:log-row-error={item.entry.status === 'error'} on:click={() => toggleGroup(item.entry.id)}>
								<span class="log-col-ts">{formatTs(item.entry.timestamp)}</span>
								<span class="log-col-type">{item.entry.type}</span>
								<span class="log-col-summary">{item.entry.summary}</span>
								<span class="log-col-outcome" class:outcome-ok={item.entry.status === 'ok'} class:outcome-err={item.entry.status === 'error'}>{outcomeLabel(item.entry)}</span>
								<span class="log-col-count log-col-count-muted">-</span>
							</div>
							{#if expandedGroupKey === item.entry.id}
								<div class="log-detail">
									<pre class="log-detail-json">{JSON.stringify(item.entry.data, null, 2)}</pre>
								</div>
							{/if}
						{/if}
					{/each}
					{#if totalPages > 1}
						<div class="log-pager">
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<span class="pager-btn" class:pager-disabled={logPage === 0} on:click={() => { if (logPage > 0) logPage--; }}>Prev</span>
							<span class="pager-info">{logPage + 1} / {totalPages}</span>
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<span class="pager-btn" class:pager-disabled={logPage >= totalPages - 1} on:click={() => { if (logPage < totalPages - 1) logPage++; }}>Next</span>
						</div>
					{/if}
				{/if}
			</div>
		</div>
	</div>
</div>

<style>
	/* ================================================================
	   PAGE
	   ================================================================ */

	.connect-page {
		min-height: 100vh;
		background: #E5E7EB;
		background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5 Q25 -3 50 5 T100 5' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 15 Q25 7 50 15 T100 15' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 25 Q25 17 50 25 T100 25' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 35 Q25 27 50 35 T100 35' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 45 Q25 37 50 45 T100 45' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 55 Q25 47 50 55 T100 55' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 65 Q25 57 50 65 T100 65' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 75 Q25 67 50 75 T100 75' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 85 Q25 77 50 85 T100 85' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3Cpath d='M0 95 Q25 87 50 95 T100 95' stroke='rgba(0,0,0,0.06)' fill='none' stroke-width='0.8'/%3E%3C/svg%3E");
		background-size: 200px 100px;
		font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
		-webkit-font-smoothing: antialiased;
		padding: 24px 32px 80px;
	}

	.connect-container {
		max-width: 900px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: 0;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 10px;
		overflow: hidden;
		min-height: calc(100vh - 140px);
	}

	/* ================================================================
	   TOP HALF: Tabs + Content
	   ================================================================ */

	.connect-top {
		flex: 0 0 auto;
		border-bottom: 1px solid #E5E7EB;
	}

	.tab-bar {
		display: flex;
		border-bottom: 1px solid #E5E7EB;
		background: #F8F9FA;
	}

	.tab {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 14px 24px;
		font-size: 12px;
		font-weight: 500;
		color: #9CA3AF;
		cursor: pointer;
		border-bottom: 2px solid transparent;
		margin-bottom: -1px;
		transition: color 120ms ease-out;
	}

	.tab:hover { color: #6B7280; }

	.tab-active {
		color: #111827;
		font-weight: 600;
		border-bottom-color: #374151;
		background: #FFFFFF;
	}

	.tab-content {
		min-height: 280px;
	}

	.tab-panel {
		padding: 24px 28px;
	}

	/* ================================================================
	   SHARED CARD GRID (email + integrations)
	   ================================================================ */

	.card-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
		margin-bottom: 20px;
	}

	.square-card {
		border: 1px solid #E5E7EB;
		border-radius: 10px;
		padding: 18px;
		background: #FFFFFF;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.square-card-future {
		opacity: 0.55;
	}

	/* ================================================================
	   UPLOAD TAB
	   ================================================================ */

	.upload-split {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 24px;
		min-height: 220px;
	}

	.upload-left {
		display: flex;
		flex-direction: column;
	}

	.upload-right {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.drop-zone-fill {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.file-list-fill {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	.file-list-fill .file-table {
		flex: 1;
		max-height: none;
		overflow-y: auto;
	}

	.expect-block {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 14px;
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 8px;
	}

	.expect-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
	}

	.submit-btn-full {
		width: 100%;
		padding: 10px 20px;
		margin-top: auto;
	}

	.collection-toggle-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 14px;
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 8px;
	}
	.collection-toggle-text { display: flex; flex-direction: column; gap: 2px; }
	.collection-toggle-label { font-size: 13px; font-weight: 600; color: #111827; }
	.collection-toggle-hint { font-size: 11px; color: #9CA3AF; }

	.toggle-switch {
		position: relative; width: 36px; height: 20px; background: #D1D5DB;
		border: none; border-radius: 10px; cursor: pointer; transition: background 200ms ease-out; padding: 0; flex-shrink: 0;
	}
	.toggle-switch.on { background: #111827; }
	.toggle-knob {
		position: absolute; top: 2px; left: 2px; width: 16px; height: 16px;
		background: #FFFFFF; border-radius: 50%; transition: transform 200ms ease-out; box-shadow: 0 1px 3px rgba(0,0,0,0.15);
	}
	.toggle-switch.on .toggle-knob { transform: translateX(16px); }

	.drop-zone {
		border: 2px dashed #D1D5DB; border-radius: 8px; padding: 28px 24px;
		text-align: center; cursor: pointer; transition: all 200ms ease-out;
		display: flex; flex-direction: column; align-items: center; background: #F9FAFB;
	}
	.drop-zone:hover { border-color: #9CA3AF; background: #F3F4F6; }
	.drop-zone.drag-over { border-color: #6B7280; background: #E5E7EB; }
	.drop-icon { color: #9CA3AF; margin-bottom: 8px; }
	.drop-title { font-size: 14px; font-weight: 600; color: #374151; margin: 0 0 4px; }
	.drop-subtitle { font-size: 12px; color: #9CA3AF; margin: 0; }

	.file-list-zone {
		display: flex; flex-direction: column; border: 1px solid #E5E7EB;
		border-radius: 8px; overflow: hidden; transition: border-color 200ms ease-out; background: #FFFFFF;
	}
	.file-list-zone.drag-over { border-color: #9CA3AF; }
	.file-table { max-height: 180px; overflow-y: auto; }
	.file-row { display: flex; align-items: center; padding: 8px 12px; gap: 8px; border-bottom: 1px solid #F3F4F6; }
	.file-row:last-child { border-bottom: none; }
	.file-name { font-size: 13px; color: #374151; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
	.file-size { font-size: 11px; color: #9CA3AF; font-family: "SF Mono", Monaco, monospace; white-space: nowrap; flex-shrink: 0; }
	.file-remove {
		width: 20px; height: 20px; border-radius: 50%; background: transparent; border: none;
		color: #9CA3AF; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; padding: 0;
	}
	.file-remove:hover { color: #EF4444; background: #FEF2F2; }
	.add-more-btn {
		padding: 8px; background: none; border: none; border-top: 1px solid #E5E7EB;
		color: #6B7280; font-size: 12px; font-weight: 500; cursor: pointer; font-family: inherit;
	}
	.add-more-btn:hover { background: #F9FAFB; color: #374151; }

	.upload-footer {
		display: flex; align-items: center; justify-content: space-between;
		margin-top: 12px; gap: 12px;
	}
	.expect-text { font-size: 12px; color: #9CA3AF; line-height: 1.4; }
	.submit-btn {
		padding: 8px 20px; background: #111827; color: #F9FAFB; border: none; border-radius: 8px;
		font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; flex-shrink: 0;
	}
	.submit-btn:hover:not(:disabled) { background: #374151; }
	.submit-btn:disabled { background: #D1D5DB; color: #9CA3AF; cursor: default; }

	.upload-progress { text-align: center; padding: 40px 24px; }
	.upload-check {
		width: 40px; height: 40px; border-radius: 50%; background: #F3F4F6; color: #374151;
		display: flex; align-items: center; justify-content: center; margin: 0 auto 12px;
	}
	.upload-check.uploading { background: #EFF6FF; color: #3B82F6; }
	.upload-status { font-size: 14px; color: #374151; font-weight: 500; margin: 0 0 4px; }
	.upload-hint { font-size: 12px; color: #9CA3AF; margin: 0; }
	.upload-okay-btn {
		margin-top: 16px; padding: 8px 24px; background: #111827; color: #F9FAFB;
		border: none; border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
	}
	.upload-okay-btn:hover { background: #374151; }
	.progress-bar-track { width: 80%; max-width: 260px; height: 5px; background: #E5E7EB; border-radius: 3px; margin: 10px auto 6px; overflow: hidden; }
	.progress-bar-fill { height: 100%; background: #111827; border-radius: 3px; transition: width 200ms ease-out; }
	.error-icon-small {
		width: 36px; height: 36px; border-radius: 50%; background: #FEF2F2; color: #EF4444;
		font-size: 18px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px;
	}
	.upload-error-msg { font-size: 13px; color: #6B7280; margin: 0 0 12px; }

	/* ================================================================
	   EMAIL TAB
	   ================================================================ */

	.config-row {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.config-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
	}

	.config-value-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.config-address {
		font-size: 14px;
		font-weight: 500;
		color: #111827;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		user-select: all;
	}

	.config-copy {
		font-size: 11px;
		font-weight: 600;
		color: #6B7280;
		cursor: pointer;
		padding: 3px 10px;
		border-radius: 4px;
		transition: all 120ms ease-out;
	}

	.config-copy:hover { color: #111827; background: #F3F4F6; }

	.config-value {
		font-size: 13px;
		color: #374151;
	}

	.status-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.status-dot.active {
		background: #10B981;
		box-shadow: 0 0 0 2.5px rgba(16, 185, 129, 0.15);
	}

	.status-dot.inactive {
		background: #D1D5DB;
	}

	/* ---- Inbox cards ---- */

	.inbox-card {
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 8px;
		padding: 16px 18px;
		margin-bottom: 14px;
	}

	.inbox-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 6px;
	}

	.inbox-head-left {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.inbox-name {
		font-size: 13px;
		font-weight: 600;
		color: #111827;
	}

	.inbox-desc {
		font-size: 12px;
		color: #6B7280;
		line-height: 1.5;
		margin: 0 0 12px;
	}

	/* ---- Address block ---- */

	.address-block {
		display: flex;
		flex-direction: column;
		gap: 6px;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 6px;
		padding: 10px 14px;
		margin-bottom: 8px;
	}

	.address-row {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.address-actions {
		display: flex;
		gap: 6px;
	}

	.action-btn {
		font-size: 11px;
		font-weight: 600;
		color: #6B7280;
		cursor: pointer;
		padding: 3px 10px;
		border-radius: 4px;
		transition: all 120ms ease-out;
		user-select: none;
	}

	.action-btn:hover { color: #111827; background: #E5E7EB; }

	.action-regen { color: #9CA3AF; }
	.action-regen:hover { color: #DC2626; background: #FEF2F2; }

	.address-label {
		font-size: 9px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		padding: 2px 6px;
		border-radius: 3px;
		flex-shrink: 0;
	}

	.label-new { background: #ECFDF5; color: #065F46; }
	.label-expiring { background: #FFFBEB; color: #92400E; }

	/* ---- Retiring address ---- */

	.address-retiring {
		background: #FFFBEB;
		border-color: #FDE68A;
	}

	.address-text-old { color: #92400E; }

	.retiring-header {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.retiring-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		flex-shrink: 0;
		background: #D1D5DB;
	}

	.retiring-dot.still-receiving {
		background: #10B981;
		box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15);
	}

	.retiring-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.retiring-status {
		font-size: 11px;
		color: #92400E;
		line-height: 1.4;
	}

	/* ---- Confirm block ---- */

	.confirm-block {
		background: #FEF2F2;
		border: 1px solid #FECACA;
		border-radius: 6px;
		padding: 10px 14px;
		margin-top: 8px;
	}

	.confirm-text {
		font-size: 12px;
		color: #991B1B;
		line-height: 1.5;
		margin: 0 0 8px;
	}

	.confirm-actions { display: flex; gap: 8px; }
	.confirm-yes { color: #DC2626; }
	.confirm-yes:hover { background: #FEE2E2; color: #991B1B; }

	/* ---- Toggle ---- */

	.toggle-track {
		position: relative;
		display: inline-block;
		width: 34px;
		height: 18px;
		border-radius: 9px;
		cursor: pointer;
		transition: background 200ms ease-out;
		flex-shrink: 0;
	}

	.toggle-track.toggle-on { background: #10B981; }
	.toggle-track.toggle-off { background: #D1D5DB; }
	.toggle-track.toggling { opacity: 0.5; cursor: default; pointer-events: none; }

	.toggle-thumb {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #FFFFFF;
		transition: transform 200ms ease-out;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
	}

	.toggle-on .toggle-thumb { transform: translateX(16px); }
	.toggle-handover { flex-shrink: 0; }

	/* Instructions (routing table) */
	.instructions {
		border-top: 1px solid #F3F4F6;
		padding-top: 16px;
	}

	.instructions-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
		display: block;
		margin-bottom: 10px;
	}

	.instruction-items {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.instruction-item {
		display: flex;
		align-items: baseline;
		gap: 12px;
		font-size: 12px;
	}

	.instruction-key {
		flex-shrink: 0;
		width: 120px;
		font-weight: 600;
		color: #374151;
	}

	.instruction-val {
		color: #6B7280;
	}

	/* ================================================================
	   INTEGRATIONS TAB
	   ================================================================ */

	.integration-head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 4px;
	}

	.integration-name {
		font-size: 13px;
		font-weight: 600;
		color: #111827;
	}

	.integration-badge {
		font-size: 9px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #9CA3AF;
		background: #F3F4F6;
		padding: 2px 6px;
		border-radius: 3px;
	}

	.integration-desc {
		font-size: 12px;
		color: #6B7280;
		margin: 0;
		line-height: 1.4;
	}

	.integration-setup {
		margin-top: 12px;
		border-top: 1px solid #F3F4F6;
		padding-top: 10px;
	}

	.setup-list {
		margin: 0;
		padding-left: 18px;
		font-size: 12px;
		color: #6B7280;
		line-height: 1.7;
	}

	.setup-list li { padding-left: 4px; }

	.intg-copy-block {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 6px;
		padding: 8px 10px;
		margin-top: 10px;
		cursor: pointer;
		transition: background 120ms ease-out;
	}
	.intg-copy-block:hover { background: #F3F4F6; }

	.intg-copy-loading {
		opacity: 0.5;
		cursor: default;
		pointer-events: none;
	}

	.intg-address {
		font-size: 11px;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}

	.intg-copy-btn {
		font-size: 10px;
		font-weight: 600;
		color: #6B7280;
		flex-shrink: 0;
		user-select: none;
	}
	.intg-copy-block:hover .intg-copy-btn { color: #111827; }

	.setup-file-label {
		display: block;
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
		margin-bottom: 6px;
	}

	.intg-secrets-note {
		font-size: 11px;
		color: #9CA3AF;
		margin: 6px 0 0;
		line-height: 1.5;
	}

	.intg-secrets-note code {
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 10px;
		background: #F3F4F6;
		padding: 1px 4px;
		border-radius: 3px;
		color: #374151;
	}

	/* ================================================================
	   BOTTOM HALF: Activity Log
	   ================================================================ */

	.connect-bottom {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 300px;
	}

	.log-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 20px;
		background: #F8F9FA;
		border-bottom: 1px solid #E5E7EB;
		flex-shrink: 0;
	}

	.log-title {
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
	}

	.log-controls {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.log-filter-label {
		font-size: 10px;
		font-weight: 500;
		color: #9CA3AF;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.log-refresh {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		color: #9CA3AF;
		cursor: pointer;
		border-radius: 4px;
		transition: color 120ms ease-out, background 120ms ease-out;
	}

	.log-refresh:hover { color: #374151; background: #E5E7EB; }

	.log-scroll {
		flex: 1;
		overflow-y: auto;
	}

	.log-row {
		display: flex;
		align-items: baseline;
		padding: 8px 20px;
		border-bottom: 1px solid #F3F4F6;
		cursor: pointer;
		transition: background 60ms ease-out;
		gap: 0;
	}

	.log-row:hover { background: #F9FAFB; }
	.log-row-expanded { background: #F9FAFB; }
	.log-row-error .log-col-summary { color: #DC2626; }

	.log-col-ts {
		width: 80px;
		flex-shrink: 0;
		font-size: 11px;
		color: #9CA3AF;
	}

	.log-col-type {
		width: 60px;
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 600;
		color: #D1D5DB;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}

	.log-col-summary {
		flex: 1;
		font-size: 12px;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}

	.log-col-outcome {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		padding: 2px 8px;
		border-radius: 3px;
		margin-left: 12px;
	}

	.outcome-ok {
		color: #059669;
		background: #ECFDF5;
	}

	.outcome-err {
		color: #DC2626;
		background: #FEF2F2;
	}

	.outcome-pending {
		color: #D97706;
		background: #FFFBEB;
	}

	/* Parent rows — slightly bolder */
	.log-row-parent .log-col-summary {
		font-weight: 600;
		color: #111827;
	}

	.log-col-count-muted { color: #E5E7EB; }

	.log-col-count {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 500;
		color: #D1D5DB;
		min-width: 16px;
		text-align: right;
		margin-left: 8px;
	}

	/* Child rows — indented, lighter */
	.log-row-child {
		padding-left: 40px;
		background: #FAFBFC;
		border-left: 2px solid #E5E7EB;
		margin-left: 20px;
		cursor: default;
	}
	.log-row-child .log-col-summary {
		font-size: 11px;
		color: #6B7280;
	}
	.log-row-child:hover { background: #F3F4F6; }

	.log-detail {
		padding: 0 20px 10px 20px;
		background: #F9FAFB;
		border-bottom: 1px solid #E5E7EB;
	}

	.log-detail-json {
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 11px;
		line-height: 1.6;
		color: #374151;
		margin: 0;
		padding: 10px 14px;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 4px;
		overflow-x: auto;
		white-space: pre;
	}

	.log-pager {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 16px;
		padding: 10px 20px;
		border-top: 1px solid #F3F4F6;
	}

	.pager-btn {
		font-size: 11px;
		font-weight: 500;
		color: #6B7280;
		cursor: pointer;
		padding: 4px 10px;
		border-radius: 4px;
		transition: all 100ms ease-out;
	}
	.pager-btn:hover { color: #111827; background: #F3F4F6; }
	.pager-disabled { color: #D1D5DB; pointer-events: none; }

	.pager-info {
		font-size: 11px;
		font-weight: 500;
		color: #9CA3AF;
	}

	.log-empty {
		padding: 48px 24px;
		text-align: center;
		font-size: 12px;
		color: #9CA3AF;
	}

	/* ================================================================
	   RESPONSIVE
	   ================================================================ */

	@media (max-width: 768px) {
		.connect-page { padding: 16px 12px 48px; }
		.tab { padding: 12px 16px; font-size: 11px; }
		.tab-panel { padding: 20px 16px; }
		.card-grid { grid-template-columns: 1fr; }
		.instruction-item { flex-direction: column; gap: 2px; }
		.instruction-key { width: auto; }
		.upload-split { grid-template-columns: 1fr; min-height: auto; }
		.upload-footer { flex-direction: column; align-items: stretch; }
		.log-col-ts { width: 60px; font-size: 10px; }
		.log-col-type { display: none; }
		.log-row { padding: 8px 12px; }
		.log-header { padding: 10px 12px; }
	}
</style>
